/**
 * Crawler Detection Utility
 * Prevents automated crawlers and bots from triggering Plaid Link sessions
 */

// Common bot/crawler user agent patterns
const BOT_PATTERNS = [
  // Search engine bots
  /googlebot/i,
  /bingbot/i,
  /yandex/i,
  /baiduspider/i,
  /duckduckbot/i,
  
  // Social media bots
  /facebookexternalhit/i,
  /twitterbot/i,
  /linkedinbot/i,
  /whatsapp/i,
  /telegrambot/i,
  
  // SEO and monitoring tools
  /ahrefsbot/i,
  /semrushbot/i,
  /mj12bot/i,
  /dotbot/i,
  /rogerbot/i,
  /screaming frog/i,
  /sitebulb/i,
  
  // Web crawlers
  /crawler/i,
  /spider/i,
  /bot/i,
  /scraper/i,
  /harvester/i,
  
  // Monitoring and analytics
  /pingdom/i,
  /uptimerobot/i,
  /monitor/i,
  /statuscake/i,
  
  // Development and testing
  /headless/i,
  /phantomjs/i,
  /selenium/i,
  /webdriver/i,
  /puppeteer/i,
  /playwright/i,
  
  // Common bot indicators
  /curl/i,
  /wget/i,
  /python-requests/i,
  /node-fetch/i,
  /axios/i,
  
  // Empty or suspicious user agents
  /^$/,
  /^[a-z0-9]{8,}$/i, // Random strings
];

// Additional bot detection methods
const BOT_INDICATORS = {
  // Check for common bot headers
  hasBotHeaders: () => {
    const headers = [
      'x-forwarded-for',
      'x-real-ip',
      'cf-connecting-ip',
      'x-forwarded-proto'
    ];
    
    return headers.some(header => 
      navigator.userAgent.includes(header) || 
      document.referrer.includes(header)
    );
  },
  
  // Check for automation indicators
  hasAutomationIndicators: () => {
    return (
      // Check for webdriver property
      navigator.webdriver === true ||
      // Check for automation properties
      window.navigator.webdriver === true ||
      // Check for headless chrome
      navigator.userAgent.includes('HeadlessChrome') ||
      // Check for common automation frameworks
      navigator.userAgent.includes('PhantomJS') ||
      navigator.userAgent.includes('Selenium') ||
      navigator.userAgent.includes('WebDriver')
    );
  },
  
  // Check for suspicious behavior patterns
  hasSuspiciousBehavior: () => {
    // Check if JavaScript is disabled (bots often don't execute JS)
    if (typeof window === 'undefined') return true;
    
    // Check for missing common browser APIs
    const missingAPIs = [
      'localStorage',
      'sessionStorage',
      'indexedDB',
      'navigator.geolocation'
    ].filter(api => !(api in window));
    
    return missingAPIs.length > 2;
  }
};

/**
 * Main function to detect if the current request is from a bot/crawler
 * @returns {boolean} True if the request appears to be from a bot
 */
export const isBot = () => {
  const userAgent = navigator.userAgent || '';
  
  // Check user agent patterns
  const isBotByUserAgent = BOT_PATTERNS.some(pattern => 
    pattern.test(userAgent)
  );
  
  if (isBotByUserAgent) {
    console.log('Bot detected by user agent:', userAgent);
    return true;
  }
  
  // Check for bot headers
  if (BOT_INDICATORS.hasBotHeaders()) {
    console.log('Bot detected by headers');
    return true;
  }
  
  // Check for automation indicators
  if (BOT_INDICATORS.hasAutomationIndicators()) {
    console.log('Bot detected by automation indicators');
    return true;
  }
  
  // Check for suspicious behavior
  if (BOT_INDICATORS.hasSuspiciousBehavior()) {
    console.log('Bot detected by suspicious behavior');
    return true;
  }
  
  return false;
};

/**
 * Enhanced bot detection with additional checks
 * @returns {Object} Object containing detection results and details
 */
export const detectBot = () => {
  const userAgent = navigator.userAgent || '';
  const referrer = document.referrer || '';
  
  const detection = {
    isBot: false,
    reason: '',
    userAgent: userAgent,
    referrer: referrer,
    timestamp: new Date().toISOString()
  };
  
  // Check user agent patterns
  for (const pattern of BOT_PATTERNS) {
    if (pattern.test(userAgent)) {
      detection.isBot = true;
      detection.reason = `Matched bot pattern: ${pattern.source}`;
      break;
    }
  }
  
  // Additional checks if not already detected
  if (!detection.isBot) {
    if (BOT_INDICATORS.hasBotHeaders()) {
      detection.isBot = true;
      detection.reason = 'Bot headers detected';
    } else if (BOT_INDICATORS.hasAutomationIndicators()) {
      detection.isBot = true;
      detection.reason = 'Automation indicators detected';
    } else if (BOT_INDICATORS.hasSuspiciousBehavior()) {
      detection.isBot = true;
      detection.reason = 'Suspicious behavior detected';
    }
  }
  
  return detection;
};

/**
 * Log bot detection for monitoring purposes
 * @param {Object} detection - Detection result object
 */
export const logBotDetection = (detection) => {
  if (detection.isBot) {
    console.warn('Bot detected and blocked from Plaid Link:', detection);
    
    // You can send this to your analytics/logging service
    // Example: sendToAnalytics('bot_detected', detection);
  }
};

export default {
  isBot,
  detectBot,
  logBotDetection
}; 